import { getPreset, RegistrationFlow, FormView } from '@account/account-portal/config'
import { useExternalAuthData } from '@account/account-portal/external-auth'
import { hideErrors as hideConsentErrors } from '@account/consents-shared-lib'
import '@account/consents/style.css'
import { useConfig } from '@crystal/config'
import { Translations, useTranslations } from '@crystal/translator'
import { useEffect, useState, FC } from 'react'

import { AccountPortalConfig } from '../../config'
import { useConsents, useViewData, useIsOneStepView, useEmphasizedSocialLoginView } from '../../lib'
import { trackSwitchToEmailInput } from '../../lib/analytics'
import styles from '../account-portal.module.scss'
import { FormContainer } from '../layout'
import { Login } from '../Login'
import { ReAuthentication } from '../ReAuthentication'
import { SocialLogin } from '../SocialLogin'

import { KmcUserData } from './KmcUserData'
import { OneStepLandingForm } from './OneStepLandingForm'
import { SubscriptionLandingForm } from './SubscriptionLandingForm'
import { TwoStepLandingForm } from './TwoStepLandingForm'

const LandingContent: FC<{ emphasizeSocialLogin?: boolean; isEmailButton?: boolean }> = ({
  emphasizeSocialLogin,
  isEmailButton,
}) => {
  const t = useTranslations<Translations>()
  const [shouldShowEmailInput, setShouldShowEmailInput] = useState<boolean>(false)
  const scenarioType = 'registration'
  const consentsState = useConsents(scenarioType)
  const { viewData, updateViewData } = useViewData()
  const { kmc } = useExternalAuthData()
  const { isKmcFlowRequired } = useConfig<AccountPortalConfig>()
  const { registrationFlow, formView } = getPreset(viewData.preset)
  const isSubscription = registrationFlow === RegistrationFlow.LightAccount && formView === FormView.OneStep
  const shouldReAuthenticate = viewData.preset === 're-authentication'

  const isOneStepView = useIsOneStepView(viewData)

  useEffect(() => {
    hideConsentErrors(scenarioType)
  }, [])

  useEffect(() => {
    updateViewData({
      touchPointConsent: consentsState,
    })
  }, [consentsState])

  const handleEmailButtonClick = () => {
    trackSwitchToEmailInput()
    setShouldShowEmailInput(true)
  }

  if (shouldReAuthenticate) {
    return <ReAuthentication />
  }

  if (isKmcFlowRequired && !kmc) {
    return <Login />
  }
  if (isSubscription) {
    return (
      <FormContainer autoId="landing-screen">
        {t('landing.title') && <h3 className={styles.title}>{t('landing.title')}</h3>}
        {t('landing.subtitle') && <p className={styles.subtitle}>{t('landing.subtitle')}</p>}
        <SubscriptionLandingForm />
      </FormContainer>
    )
  }

  return (
    <FormContainer autoId="landing-screen">
      {isKmcFlowRequired && kmc ? (
        <KmcUserData />
      ) : (
        <>
          <h3 className={styles.title}>{t('landing.title')}</h3>
          {viewData.preset === 'checkout-inline' && <p className={styles['form-title']}>{t('landing.chk.title')}</p>}
          <p className={styles.subtitle}>{t('landing.subtitle')}</p>
          <p className={styles['form-title']}>{t('landing.form.title')}</p>
          <SocialLogin
            fullWidthButtons
            isEmailButton={!shouldShowEmailInput && isEmailButton}
            emphasizeSocialLogin={emphasizeSocialLogin}
            persistentLogin={viewData.persistentLogin}
          />
        </>
      )}
      {isOneStepView ? (
        <OneStepLandingForm />
      ) : (
        <TwoStepLandingForm
          isEmailButton={!shouldShowEmailInput && isEmailButton}
          onEmailButtonClick={handleEmailButtonClick}
        />
      )}
    </FormContainer>
  )
}

export const Landing = () => {
  const loginFormView = useEmphasizedSocialLoginView()

  // Render different screens for A/B tests
  switch (loginFormView) {
    case '1':
      return <LandingContent emphasizeSocialLogin />
    case '2':
      return <LandingContent emphasizeSocialLogin isEmailButton />
    default:
      return <LandingContent />
  }
}
